import React, { useState } from 'react';
import { Typography, Select, Tag, Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { PlayingMember } from '../../model/members/PlayingMember';
import { MemberOptionLabel, useMemberSearch } from '../common/memberSearch';

interface AdditionalMemberSelectorProps {
  playingMembers: string[];
  // Superset of active members, used only to resolve already-selected member
  // names to ids (the dropdown options come from server-side search).
  members: PlayingMember[];
  membershipId: string;
  onMembersChange: (membershipIds: string[]) => void;
  onRemoveMember: (memberName: string) => void;
  // When true, render only the full-width control (no inline "Playing Members:"
  // label), so a caller can place it inside its own vertical/labelled form.
  controlOnly?: boolean;
}

const AdditionalMemberSelector: React.FC<AdditionalMemberSelectorProps> = ({
  playingMembers,
  members,
  membershipId,
  onMembersChange,
  onRemoveMember,
  controlOnly = false
}) => {
  // Default to active members only; unticking widens the search to everyone.
  const [activeOnly, setActiveOnly] = useState(true);
  const { results, search, reset } = useMemberSearch(activeOnly);

  const handleActiveOnlyChange = (e: CheckboxChangeEvent) => {
    setActiveOnly(e.target.checked);
  };

  // Currently-selected members, resolved from their names via the members list.
  const selectedIds = playingMembers
    .map((name) => members?.find((m) => m.name === name)?.membershipId)
    .filter((id): id is string => id !== undefined);

  // Server-side search results minus the main booking member and anyone already
  // picked; results arrive ranked, so preserve their order.
  const options = results
    .filter(
      (r) =>
        r.membershipId !== membershipId &&
        !selectedIds.includes(r.membershipId) &&
        !playingMembers.includes(r.name)
    )
    .map((r) => ({
      label: <MemberOptionLabel member={r} />,
      value: r.membershipId,
    }));

  const control = (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: controlOnly ? "stretch" : "flex-end",
          gap: "8px",
          width: controlOnly ? "100%" : "200px",
        }}
      >
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder="Search by name, id, phone, legacy id or email"
          value={selectedIds}
          onChange={onMembersChange}
          onSelect={() => {
            reset();
            const selectElement = document.activeElement as HTMLElement;
            selectElement?.blur();
          }}
          onBlur={reset}
          options={options}
          // Matching + ranking happen server-side; don't re-filter or re-order.
          filterOption={false}
          onSearch={search}
          defaultActiveFirstOption
          notFoundContent={null}
          showSearch
          maxTagCount={0}
        />
        <Checkbox
          checked={activeOnly}
          onChange={handleActiveOnlyChange}
          data-testid="additional-member-active-only"
          style={{ alignSelf: controlOnly ? "flex-start" : "flex-end" }}
        >
          Active only
        </Checkbox>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "flex-start", width: "100%" }}>
          {playingMembers.filter((memberName, index, self) =>
            index === self.findIndex((t) => t === memberName)
          ).map((memberName, index) => (
            <Tag
              key={memberName}
              closable
              onClose={() => onRemoveMember(memberName)}
              style={{
                margin: "2px",
                borderRadius: "16px",
                padding: "4px 12px",
                backgroundColor: `hsl(${(index * 30) % 360}, 70%, 90%)`,
                border: `1px solid hsl(${(index * 30) % 360}, 70%, 80%)`,
                color: `hsl(${(index * 30) % 360}, 70%, 30%)`,
              }}
            >
              {memberName}
            </Tag>
          ))}
        </div>
      </div>
  );

  if (controlOnly) {
    return control;
  }

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
      <Typography.Text><strong>Playing Members:</strong></Typography.Text>
      {control}
    </div>
  );
};

export default AdditionalMemberSelector;
